/* RESPONSIBLE TEAM: team-standalone */
import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router';
import type IntlService from 'embercom/services/intl';
import { ResponseError } from 'embercom/lib/inbox/requests';
import type Transition from '@ember/routing/transition';
import type PlaygroundApiService from 'embercom/services/playground-api';
import { type PlaygroundSummary } from 'embercom/lib/fin-playground/playground-summary';

export default class TestingStandaloneRoute extends Route {
  @service declare appService: any;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare permissionsService: $TSFixMe;
  @service declare playgroundApi: PlaygroundApiService;

  analytics = {
    section: 'standalone',
    place: 'standalone.testing',
  };

  get titleToken() {
    return this.intl.t('standalone.navbar.test');
  }

  async beforeModel(transition: Transition) {
    if (this.appService.app.canUseFinPlaygroundRedesign) {
      try {
        await this.playgroundApi.validateAdminPermissions();
      } catch (err) {
        if (err instanceof ResponseError && err.response.status === 401) {
          // The permissions service will handle showing the modal
          // and redirecting when there is no from route
          await this.permissionsService.cancelTransitionAndShowPermissionModal(
            'can_access_all_conversations',
            transition,
            'apps.app.standalone.deploy.overview',
          );

          // If there is a from route we still want to transition to it
          // Otherwise we will stay on the current route with a loading spinner
          if (transition.from?.name) {
            this.router.transitionTo(transition.from.name);
          }

          return;
        }
        throw err;
      }
    }
  }

  async model() {
    if (this.appService.app.canUseFeature('fin-playground-groups')) {
      // If we can use groups, request the group summary and return the result
      let { playgroundSummaries, questionGenerationSummary } =
        await this.playgroundApi.fetchGroupsSummary();

      return {
        playgroundSummaries,
        questionGenerationSummary,
      };
    } else {
      return {
        playgroundSummaries: [],
        questionGenerationSummary: null,
      };
    }
  }

  afterModel(resolvedModel: { playgroundSummaries: PlaygroundSummary[] }): void {
    let { playgroundSummaries } = resolvedModel;

    if (playgroundSummaries.length > 0) {
      // If there are groups, transition to the test route using the first group id
      let firstGroupSummary = playgroundSummaries[0];
      this.router.transitionTo('apps.app.standalone.testing.test', firstGroupSummary.id, {
        skipSummaryLoadAndValidation: true,
      });
    } else {
      // If there are no groups, remain on the index route
      return;
    }
  }
}
